import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Moon, Sun, LogOut, Drama, ChevronDown } from "lucide-react";
import { InstallButton } from "@/components/InstallButton";
import { BackButton } from "@/components/BackButton";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

// The word on the dropdown that holds the two interactive pages. Change it here.
const DISCOVER_LABEL = "Discover";

const TIKTOK_URL = "https://www.tiktok.com/@mytheatreuk";

function TikTokIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M19.6 6.7a4.8 4.8 0 0 1-3.8-4.3V2h-3.3v13.2a2.8 2.8 0 1 1-2.8-2.8c.3 0 .6 0 .8.1V9.1a6.1 6.1 0 1 0 5.3 6.1V8.9a8 8 0 0 0 4.700 1.500V7.100a4.800 4.800 0 0 1-.9-.4z" />
    </svg>
  );
}

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/shows", label: "Browse Shows" },
  { to: "/casts", label: "Casts" },
  { to: "/watchlist", label: "My Theatre" },
  { to: "/news", label: "News" },
];

// "label" is shown in the dropdown, "short" in the scrolling bar on phones.
const discoverLinks = [
  { to: "/quiz", label: "Which role are you?", short: "Role Quiz" },
  { to: "/find-a-show", label: "Which show should I see?", short: "Find a Show" },
  { to: "/trivia", label: "Daily Theatre Trivia", short: "Trivia" },
];

const moreLinks = [
  { to: "/about", label: "About Us" },
  { to: "/theatres", label: "Theatre Map" },
  { to: "/theatre-guide", label: "Theatre A-Z" },
  { to: "/contact", label: "Contact Us" },
  { to: "/privacy", label: "Privacy Policy" },
];

function NavDropdown({ label, items, testId, width = "w-48" }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isActive = items.some((l) => location.pathname === l.to);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={testId}
        aria-haspopup="true"
        aria-expanded={open}
        className={`whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium transition-colors xl:px-4 ${
          isActive ? "bg-[#9E1B28] text-amber-50" : "text-amber-100/70 hover:text-amber-50 hover:bg-white/5"
        }`}
      >
        {label} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute right-0 mt-2 ${width} overflow-hidden rounded-xl border border-amber-900/20 bg-[#161118] py-1 shadow-xl`}>
          {items.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`block px-4 py-2.5 text-sm transition-colors ${
                location.pathname === l.to
                  ? "bg-[#9E1B28] text-amber-50"
                  : "text-amber-100/80 hover:bg-white/5 hover:text-amber-50"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function Navbar() {
  const { user, logout, dark, setDark } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [panel, setPanel] = useState(null); // "discover", "more" or null (phone menu)

  useEffect(() => setPanel(null), [location.pathname]);

  const panelItems = panel === "discover" ? discoverLinks : panel === "more" ? moreLinks : [];

  return (
    <header className="sticky top-0 z-50 border-b border-amber-900/20 bg-[#0D0A0E]/90 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
      <div className="flex items-center">
        <BackButton />
        <Link to="/" data-testid="nav-logo" className="flex shrink-0 items-center gap-2 text-amber-100">
          <Drama className="h-6 w-6 text-[#C89B3C]" />
          <span className="whitespace-nowrap font-serif text-xl font-semibold tracking-tight">My Theatre</span>
        </Link>
      </div>

        <nav className="hidden items-center gap-1 md:flex">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium transition-colors xl:px-4 ${
                  isActive ? "bg-[#9E1B28] text-amber-50" : "text-amber-100/70 hover:text-amber-50 hover:bg-white/5"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}

          <NavDropdown label={DISCOVER_LABEL} items={discoverLinks} testId="nav-discover" width="w-64" />
          <NavDropdown label="More" items={moreLinks} testId="nav-more" />

          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-2.5 py-2 text-sm font-medium transition-colors xl:px-4 ${
                  isActive ? "bg-[#C89B3C] text-black" : "text-[#C89B3C] hover:bg-white/5"
                }`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          <InstallButton />
          <a
            href={TIKTOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="My Theatre on TikTok"
            data-testid="tiktok-link"
            className="rounded-full p-2 text-amber-100/70 transition-colors hover:bg-white/10 hover:text-amber-50"
          >
            <TikTokIcon className="h-4 w-4" />
          </a>
          <button
            data-testid="theme-toggle"
            onClick={() => setDark(!dark)}
            className="rounded-full p-2 text-amber-100/70 transition-colors hover:bg-white/10 hover:text-amber-50"
            aria-label="Toggle theme"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          {user && <ChangePasswordDialog />}
          {user ? (
            <button
              data-testid="logout-button"
              onClick={async () => { await logout(); nav("/"); }}
              className="flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-2 text-sm text-amber-100/80 transition-colors hover:bg-white/10"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden xl:inline">Sign out</span>
            </button>
          ) : (
            <Link
              to="/login"
              data-testid="login-button"
              className="rounded-full bg-[#9E1B28] px-4 py-2 text-sm font-medium text-amber-50 hover:bg-[#B22232]"
            >
              Sign in
            </Link>
          )}
        </div>
      </div>

            {/* mobile nav */}
            <div className="md:hidden">
        <nav className="flex items-center gap-1 overflow-x-auto border-t border-amber-900/20 px-4 py-2">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${
                  isActive ? "bg-[#9E1B28] text-amber-50" : "text-amber-100/70"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}

          {[
            { key: "discover", label: DISCOVER_LABEL, items: discoverLinks },
            { key: "more", label: "More", items: moreLinks },
          ].map((g) => {
            const active = g.items.some((l) => location.pathname === l.to);
            const isOpen = panel === g.key;
            return (
              <button
                key={g.key}
                type="button"
                onClick={() => setPanel(isOpen ? null : g.key)}
                aria-expanded={isOpen}
                className={`flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${
                  active || isOpen ? "bg-[#9E1B28] text-amber-50" : "text-amber-100/70"
                }`}
              >
                {g.label} <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
            );
          })}

          {user?.role === "admin" && (
            <NavLink to="/admin" className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium ${isActive ? "bg-[#C89B3C] text-black" : "text-[#C89B3C]"}`}>
              Admin
            </NavLink>
          )}
        </nav>

        {panel && (
          <div className="absolute inset-x-0 top-full z-50 border-b border-amber-900/20 bg-[#161118] px-4 py-2 shadow-xl">
            {panelItems.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`block rounded-lg px-3 py-3 text-sm ${
                  location.pathname === l.to ? "bg-[#9E1B28] text-amber-50" : "text-amber-100/80"
                }`}
              >
                {l.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    </header>
  );
}